import type { ReactNode } from "react";

/**
 * Tiny markdown renderer for creator briefs. Supports:
 *   #, ##, ### headings
 *   --- horizontal rule
 *   * / - bullets
 *   _italic_, **bold**
 *   blank-line paragraph breaks
 * We control the input shape (briefs come from our template + user edits),
 * so this is intentionally limited and safe (no raw HTML).
 */
export function Markdown({ source }: { source: string }) {
  const blocks = source.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return (
    <div className="prose-brief grid gap-3">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

function renderBlock(block: string, key: number): ReactNode {
  const trimmed = block.trim();
  if (!trimmed) return null;

  if (trimmed === "---") {
    return <hr key={key} className="border-t border-border my-2" />;
  }

  let m: RegExpMatchArray | null;
  if ((m = trimmed.match(/^###\s+(.*)$/))) {
    return (
      <h3 key={key} className="font-display text-base font-semibold tracking-tightish">
        {renderInline(m[1]!)}
      </h3>
    );
  }
  if ((m = trimmed.match(/^##\s+(.*)$/))) {
    return (
      <h2 key={key} className="font-display text-xl font-semibold tracking-tightish mt-2">
        {renderInline(m[1]!)}
      </h2>
    );
  }
  if ((m = trimmed.match(/^#\s+(.*)$/))) {
    return (
      <h1 key={key} className="font-display text-2xl font-semibold tracking-tighter">
        {renderInline(m[1]!)}
      </h1>
    );
  }

  // bullet list (lines starting with -, *, or +)
  const lines = trimmed.split("\n");
  if (lines.every((l) => /^[-*+]\s+/.test(l))) {
    return (
      <ul key={key} className="list-disc pl-6 grid gap-1">
        {lines.map((l, j) => (
          <li key={j}>{renderInline(l.replace(/^[-*+]\s+/, ""))}</li>
        ))}
      </ul>
    );
  }

  return (
    <p key={key} className="text-ink/80 leading-relaxed whitespace-pre-line">
      {renderInline(trimmed)}
    </p>
  );
}

function renderInline(text: string): ReactNode[] {
  // Tokenize **bold**, _italic_, leave the rest as text.
  const parts: ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|_[^_]+_)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const tok = match[0]!;
    if (tok.startsWith("**")) {
      parts.push(
        <strong key={key++} className="font-semibold text-ink">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else {
      parts.push(
        <em key={key++} className="italic text-muted">
          {tok.slice(1, -1)}
        </em>,
      );
    }
    last = match.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
